import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {mobile} from 'shared/Breakpoints';
import {useIsTouchDevices} from 'shared/helpers/DeviceSize';
import {RoomScene} from 'shared/components/Header/RoomPlanner/NavigationGuide/RoomScene';
import {Gesture} from 'shared/components/Header/RoomPlanner/NavigationGuide/Gesture';
import {useDioramaControls} from 'shared/components/Header/RoomPlanner/NavigationGuide/useDioramaControls';
import {
    FOCUS_STAGES,
    FOCUS_STEP,
    GESTURE_STEPS,
    Gesture as GestureName,
    LOOP_DURATION,
    getCamera,
    getCopy,
    getGestureState,
    getStepOffset,
    getStepState,
} from 'shared/components/Header/RoomPlanner/NavigationGuide/scene';

const STEPS = ['1. Orbit', '2. Pan', '3. Zoom', '4. Focus'];
const MAX_FRAME = 80;
const GESTURE_INDEX = new Map(Object.entries(GESTURE_STEPS));

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface NavigationGuideProps {
    onDismiss: () => void;
    onWatchVideos: () => void;
}

export const NavigationGuide = ({
    onDismiss,
    onWatchVideos,
}: NavigationGuideProps) => {
    const isTouchDevice = useIsTouchDevices();
    const [touch, setTouch] = useState(isTouchDevice);
    const [playing, setPlaying] = useState(() => !prefersReducedMotion());
    const [elapsed, setElapsed] = useState(0);
    const lastFrame = useRef<number | null>(null);
    const elapsedRef = useRef(elapsed);

    elapsedRef.current = elapsed;

    const getDemoCamera = useCallback(
        () => getCamera(getStepState(elapsedRef.current)),
        []
    );

    const handleGesture = useCallback(
        (gesture: GestureName, pointerType: string) => {
            const gestureStep = GESTURE_INDEX.get(gesture) ?? 0;
            const currentStep = getStepState(elapsedRef.current).index;

            setTouch(pointerType === 'touch' || pointerType === 'pen');

            if (currentStep !== gestureStep && currentStep !== FOCUS_STEP) {
                setElapsed(getStepOffset(gestureStep));
            }
        },
        []
    );

    const diorama = useDioramaControls({
        getDemoCamera,
        autoReturn: playing,
        onGesture: handleGesture,
    });
    const animating = playing && !diorama.manual;

    useEffect(() => {
        if (!animating) {
            lastFrame.current = null;
            return;
        }

        let frame: number;
        const tick = (now: number) => {
            if (lastFrame.current !== null && !document.hidden) {
                const delta = Math.min(now - lastFrame.current, MAX_FRAME);
                setElapsed((value) => (value + delta) % LOOP_DURATION);
            }

            lastFrame.current = now;
            frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(frame);
            lastFrame.current = null;
        };
    }, [animating]);

    const step = getStepState(elapsed);
    const camera = diorama.camera ?? getCamera(step);
    const {title, caption} = getCopy(step.index, step.depth, touch, step.stage);
    const gesture = getGestureState(step);
    const focusing = step.index === FOCUS_STEP;
    const showCrosshair =
        focusing && !diorama.camera && step.stage === FOCUS_STAGES.pan;

    const jumpTo = useCallback(
        (index: number) => () => setElapsed(getStepOffset(index)),
        []
    );

    const toggleMode = useCallback(() => {
        setTouch((value) => !value);
        setElapsed(0);
    }, []);

    const togglePlaying = useCallback(() => {
        setPlaying((value) => !value);
    }, []);

    return (
        <Guide aria-label="Room navigation tutorial">
            <Header>
                <div>
                    <Eyebrow>3D Room Planner</Eyebrow>
                    <Title>Find your way around</Title>
                </div>
                <ModeChip
                    type="button"
                    onClick={toggleMode}
                    aria-label={`Showing ${
                        touch ? 'touch' : 'mouse'
                    } controls. Switch to ${touch ? 'mouse' : 'touch'}`}>
                    {touch ? 'Touch' : 'Mouse'}
                </ModeChip>
            </Header>
            <Scene
                {...diorama.handlers}
                svgRef={diorama.svgRef}
                interactive
                dragging={diorama.dragging}
                data-manual={diorama.manual ? 'true' : 'false'}
                camera={camera}
                focus={focusing}
                crosshair={showCrosshair}
                label={`${title}. ${caption}.`}
            />
            <GestureArea>
                <Gesture {...gesture} touch={touch} label={title} />
            </GestureArea>
            <Copy aria-live="polite">
                <Caption>{title}</Caption>
                <SubCaption>{caption}</SubCaption>
            </Copy>
            <Steps aria-label="Navigation demonstrations">
                {STEPS.map((label, index) => (
                    <Step
                        key={label}
                        type="button"
                        aria-pressed={index === step.index}
                        onClick={jumpTo(index)}>
                        {label}
                        <Progress
                            style={{
                                width: `${
                                    index === step.index
                                        ? step.phase * 100
                                        : index < step.index
                                          ? 100
                                          : 0
                                }%`,
                            }}
                        />
                    </Step>
                ))}
            </Steps>
            <Footer>
                <PauseButton type="button" onClick={togglePlaying}>
                    {playing ? 'Pause' : 'Play'}
                </PauseButton>
                <VideosLink type="button" onClick={onWatchVideos}>
                    <PlayIcon aria-hidden="true">▶</PlayIcon>
                    Watch getting started videos
                </VideosLink>
                <DoneButton type="button" onClick={onDismiss}>
                    Got it
                </DoneButton>
            </Footer>
        </Guide>
    );
};

const Guide = styled.section`
    color: ${({theme}) => theme.colors.primary.main};
    max-width: 440px;
    margin: 0 auto;

    button {
        font: inherit;
        cursor: pointer;
    }

    button:focus-visible {
        outline: 3px solid ${({theme}) => theme.colors.secondary.main};
        outline-offset: 3px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

const Eyebrow = styled.div`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #60788e;
`;

const Title = styled.div`
    margin: 5px 0 0;
    font-size: 23px;
    font-weight: 600;
    letter-spacing: -0.4px;

    ${mobile`font-size: 20px;`}
`;

const ModeChip = styled.button`
    font-size: 11px !important;
    color: #60788e;
    background: #fff;
    padding: 6px 10px;
    border: 1px solid #dce6ed;
    border-radius: 20px;

    &:hover {
        border-color: ${({theme}) => theme.colors.secondary.main};
    }
`;

const Scene = styled(RoomScene)`
    height: 238px;
`;

const GestureArea = styled.div`
    margin-top: -10px;
    background: #f3f8fc;
    border-radius: 14px;
`;

const Copy = styled.div`
    text-align: center;
    padding: 15px 18px 18px;
    min-height: 91px;
`;

const Caption = styled.div`
    font-size: 17px;
    font-weight: 600;
    margin: 0 0 4px;
`;

const SubCaption = styled.p`
    margin: 0;
    color: #60788e;
    font-size: 13px;
`;

const Steps = styled.div`
    display: flex;
    gap: 8px;
`;

const Step = styled.button`
    position: relative;
    flex: 1;
    min-width: 0;
    border: 0;
    border-radius: 8px;
    background: #f3f8fc;
    color: #60788e;
    padding: 10px 2px 13px;
    overflow: hidden;
    font-size: 12px !important;

    &[aria-pressed='true'] {
        color: ${({theme}) => theme.colors.primary.main};
    }
`;

const Progress = styled.span`
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background: ${({theme}) => theme.colors.secondary.main};
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding-top: 16px;

    ${mobile`flex-wrap: wrap;`}
`;

const PauseButton = styled.button`
    border: 0;
    background: transparent;
    color: #60788e;
    padding: 10px 0;
    min-width: 45px;
    text-align: left;
    font-size: 12px !important;
`;

const VideosLink = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    border: 0;
    background: transparent;
    padding: 6px 0;
    font-size: 13px !important;
    font-weight: 600;
    color: ${({theme}) => theme.colors.secondary.main};

    &:hover {
        text-decoration: underline;
    }

    ${mobile`
        order: 3;
        flex-basis: 100%;
        justify-content: center;
    `}
`;

const PlayIcon = styled.span`
    display: grid;
    place-items: center;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    padding-left: 2px;
    font-size: 10px;
    color: #fff;
    background: ${({theme}) => theme.colors.secondary.main};
`;

const DoneButton = styled.button`
    border: 0;
    border-radius: 9px;
    padding: 10px 18px;
    font-weight: 600;
    color: #fff;
    background: ${({theme}) => theme.colors.primary.main};
`;
